/* KiraSilv Mobile: small enhancements; Trade owns product pages, search and cart. */
(() => {
  'use strict';
  if (customElements.get('kira-mobile')) return;
  class KiraMobile extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.pending = false;
      this.leaving = false;
      const signal = this.events.signal;
      this.addEventListener('submit', event => this.submit(event), {signal});
      this.addEventListener('change', event => {
        if (event.target.matches('[data-ksm-choice]')) this.updateDuo();
      }, {signal});
      window.addEventListener('pageshow', event => {
        if (event.persisted) this.resetForms();
      }, {signal});
      this.setupMotion();
      this.setupRail(signal);
      this.querySelectorAll('[data-ksm-duo-image]').forEach(image => { image.dataset.fallback = image.src; });
      const duo = this.querySelector('[data-ksm-duo]');
      if (duo && window.fetch) { duo.hidden = false; this.updateDuo(); }
    }
    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.railFrame);
      this.querySelectorAll('.ksm-will-reveal').forEach(el => el.classList.remove('ksm-will-reveal'));
      this.resetForms();
    }
    setupMotion() {
      if (window.Shopify?.designMode || !('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.observer = new IntersectionObserver(entries => {
        for (const entry of entries) if (entry.isIntersecting) {
          entry.target.classList.add('ksm-visible'); this.observer.unobserve(entry.target);
        }
      }, {threshold:0.08});
      this.querySelectorAll('[data-ksm-reveal]').forEach(el => { el.classList.add('ksm-will-reveal'); this.observer.observe(el); });
    }
    setupRail(signal) {
      const rail = this.querySelector('[data-ksm-rail]');
      if (!rail) return;
      const controls = this.querySelector('[data-ksm-rail-controls]');
      const update = () => {
        const scrollable = rail.scrollWidth > rail.clientWidth + 3;
        controls.hidden = !scrollable;
        const children = [...rail.children];
        const left = rail.getBoundingClientRect().left;
        const index = children.reduce((best, node, i) => Math.abs(node.getBoundingClientRect().left-left) < Math.abs(children[best].getBoundingClientRect().left-left) ? i : best, 0);
        controls.querySelector('[data-ksm-rail-status]').textContent = `${index + 1} / ${children.length}`;
        controls.querySelector('[data-ksm-scroll="-1"]').disabled = rail.scrollLeft <= 3;
        controls.querySelector('[data-ksm-scroll="1"]').disabled = rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 3;
      };
      const scroll = direction => {
        const gap = parseFloat(getComputedStyle(rail).gap) || 0;
        const amount = (rail.firstElementChild?.getBoundingClientRect().width || rail.clientWidth) + gap;
        rail.scrollBy({left:direction*amount, behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      };
      controls.addEventListener('click', event => {
        const button = event.target.closest('[data-ksm-scroll]');
        if (button) scroll(Number(button.dataset.ksmScroll));
      }, {signal});
      rail.addEventListener('scroll', () => {
        cancelAnimationFrame(this.railFrame); this.railFrame = requestAnimationFrame(update);
      }, {passive:true, signal});
      rail.addEventListener('keydown', event => {
        if (event.target !== rail || !['ArrowLeft','ArrowRight'].includes(event.key)) return;
        event.preventDefault(); scroll(event.key === 'ArrowRight' ? 1 : -1);
      }, {signal});
      document.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('.ksm-collection');
        if (card && rail.contains(card)) card.scrollIntoView({block:'nearest',inline:'nearest',behavior:'auto'});
      }, {signal});
      if ('ResizeObserver' in window) { this.resizeObserver = new ResizeObserver(update); this.resizeObserver.observe(rail); }
      else window.addEventListener('resize', update, {signal});
      update();
    }
    money(cents) {
      const currency = window.Shopify?.currency?.active || this.dataset.currency || 'USD';
      try { return new Intl.NumberFormat(this.dataset.locale || 'en', {style:'currency',currency}).format(cents / 100); }
      catch { return `${(cents/100).toFixed(2)} ${currency}`; }
    }
    choices() { return [...this.querySelectorAll('[data-ksm-choice]')].map(select => select.selectedOptions[0]); }
    updateDuo() {
      const button = this.querySelector('[data-ksm-duo-submit]');
      if (!button) return;
      const selections = this.choices();
      selections.forEach((option, index) => {
        const image = this.querySelectorAll('[data-ksm-duo-image]')[index];
        image.src = option?.dataset.image || image.dataset.fallback;
        image.alt = option?.dataset.title || '';
        const detail = this.querySelectorAll('[data-ksm-choice-detail]')[index];
        if (detail) { detail.hidden = !option?.value; detail.textContent = option?.value ? option.textContent.trim() : ''; }
      });
      const valid = selections.length === 2 && selections.every(option => /^\d+$/.test(option?.value || ''));
      this.querySelector('[data-ksm-total]').textContent = valid ? this.money(selections.reduce((sum,option) => sum + Number(option.dataset.cents),0)) : '—';
      button.disabled = !valid || this.pending;
      this.querySelectorAll('[data-ksm-choice]').forEach(select => { select.disabled = this.pending; });
    }
    resetForms() {
      this.pending = false; this.leaving = false;
      this.querySelectorAll('[data-ksm-native-add]').forEach(form => {
        delete form.dataset.pending;
        const button = form.querySelector('button[type="submit"]');
        if (button) { button.disabled = false; button.querySelector('span').textContent = 'Add to bag'; }
      });
      const submit = this.querySelector('[data-ksm-duo-submit] span');
      if (submit) submit.textContent = 'Add both to bag';
      const status = this.querySelector('[data-ksm-status]');
      if (status) status.textContent = '';
      this.updateDuo();
    }
    submit(event) {
      const form = event.target;
      if (form.matches('[data-ksm-native-add]')) {
        if (form.dataset.pending) { event.preventDefault(); return; }
        if (!form.checkValidity()) return;
        form.dataset.pending = 'true';
        const button = form.querySelector('button[type="submit"]');
        button.disabled = true; button.querySelector('span').textContent = 'Adding…';
        // Do not intercept: Shopify handles the native POST and displays Trade's cart.
        return;
      }
      if (form.matches('[data-ksm-duo]')) {
        event.preventDefault();
        if (!this.pending && form.reportValidity()) this.addDuo();
      }
    }
    async addDuo() {
      const selections = this.choices();
      if (this.pending || selections.length !== 2 || selections.some(option => !/^\d+$/.test(option?.value || ''))) return;
      const status = this.querySelector('[data-ksm-status]');
      const checkBag = this.querySelector('[data-ksm-check-bag]');
      const items = new Map();
      for (const option of selections) {
        const quantity = (items.get(option.value)?.quantity || 0) + 1;
        if (option.dataset.max && quantity > Number(option.dataset.max)) {
          status.textContent = 'This option allows one piece per order. Please choose a different second piece.';
          return;
        }
        items.set(option.value, {id:option.value,quantity});
      }
      const run = this.events;
      const current = () => this.isConnected && this.events === run;
      const request = new AbortController();
      const abort = () => request.abort();
      run.signal.addEventListener('abort',abort,{once:true});
      const timer = setTimeout(abort,20000);
      this.pending = true;
      this.updateDuo();
      this.querySelector('[data-ksm-duo-submit] span').textContent = 'Adding your duo…';
      status.textContent = ''; checkBag.hidden = true;
      try {
        const root = (window.Shopify?.routes?.root || this.dataset.root || '/').replace(/\/?$/, '/');
        const response = await fetch(`${root}cart/add.js`, {
          method:'POST', credentials:'same-origin', signal:request.signal,
          headers:{'Content-Type':'application/json','Accept':'application/json'},
          body:JSON.stringify({items:[...items.values()]})
        });
        const result = await response.json();
        if (!response.ok || result.status >= 400) throw new Error(typeof result.description === 'string' ? result.description : 'Please check availability and your bag before trying again.');
        if (!current()) return;
        this.leaving = true;
        status.textContent = 'Added. Opening your bag…';
        window.location.assign(this.dataset.cart);
      } catch (error) {
        if (!current()) return;
        status.textContent = error.name === 'AbortError' || error instanceof TypeError || error instanceof SyntaxError ? 'We could not confirm the update. Check your bag before trying again.' : error.message;
        checkBag.hidden = false;
      } finally {
        clearTimeout(timer); run.signal.removeEventListener('abort',abort);
        if (current() && !this.leaving) {
          this.pending = false;
          this.querySelector('[data-ksm-duo-submit] span').textContent = 'Add both to bag';
          this.updateDuo();
        }
      }
    }
  }
  customElements.define('kira-mobile', KiraMobile);
})();
